import { Spacing } from '@persona-id/design-tokens';
import { useEffect } from 'react';
import { useTranslation, WithTranslation } from 'react-i18next';
import { Redirect } from 'react-router';
import Markdown from 'shared/components/Markdown';
import styled from 'styled-components';

import Spacer from 'design-system/components/Spacer';
import IconCircleFilledCheck from 'images/icons/circle-filled-check.svg';
import IconCircleFilledX from 'images/icons/circle-filled-x.svg';
import IconCamera from 'images/icons/inquiry/camera.svg';
import Button, { ButtonType } from 'inquiry/components/Button';
import CustomAssetWithFallback from 'inquiry/components/CustomAssetWithFallback';
import ThemedParagraph from 'inquiry/components/ThemedParagraph';
import ThemedTitle from 'inquiry/components/ThemedTitle';
import { useStepSelfieStyleContext } from 'inquiry/context/StepSelfieStyleContext';
import EventsClient from 'lib/clients/EventsClient';
import { StepSelfieConfig } from 'lib/interfaces/StepsConfig';
import { SelfieMatch } from 'lib/interfaces/verificationConfig';
import { captureMessage } from 'lib/reporting';
import { assertNever } from 'lib/utils/types';
import ButtonInputCapture from 'verify/components/ButtonInputCapture';
import CenteredPictograph from 'verify/components/CenteredPictograph';
import CustomizablePictograph from 'verify/components/CustomizablePictograph';
import { FullScreenLoadingSpinner } from 'verify/components/LoadingSpinner';
import PositionableButtonContainer from 'verify/components/PositionableButtonContainer/PositionableButtonContainer';
import SelfiePictograph from 'verify/components/SelfiePictograph';
import { HolderClient } from 'verify/lib/clients/HolderClient';
import {
  SelfieCaptureOptionDesktop,
  SelfieCaptureOptionMobile,
} from 'verify/lib/interfaces/CaptureOption';
import PromptOptions from 'verify/lib/interfaces/PromptOptions';
import { SelfieStep } from 'verify/lib/interfaces/selfie';
import { MIN_HEIGHTS } from 'verify/styles/variables';

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const Header = styled('div')`
  min-height: ${MIN_HEIGHTS.header};
  margin-bottom: ${Spacing[6]};
`;

const OptionButton = styled(Button)`
  margin-bottom: ${Spacing[4]};
  :last-of-type {
    margin-bottom: 0;
  }
`;

const IconCameraStyled = styled(IconCamera)`
  path[stroke] {
    stroke: ${(props) => props.theme.colorFontButtonPrimary};
  }
`;

const CaptureOptionsContainer = styled.div`
  width: 100%;
`;

interface InquirySelfiePromptViewProps {
  assets: StepSelfieConfig['assets']['prompt-page'];
  localizations: StepSelfieConfig['localizations']['prompt-page'];
  handleSelectImage: (file: File) => void;
  handleStartRecording: () => Promise<void>;
  handleDeviceHandoff: () => void;
  enabledCaptureOptions: Array<SelfieCaptureOptionDesktop | SelfieCaptureOptionMobile>;
  allowStillfieFallback: boolean;
  isProcessing: PromptOptions | null;
  currentStep: SelfieStep;
  requireSelfieMatch: SelfieMatch;
  skipPromptPage: boolean;
  uiReady: boolean;
}

export default function InquirySelfiePromptView(props: InquirySelfiePromptViewProps) {
  const { enabledCaptureOptions, allowStillfieFallback } = props;
  const [t, i18n] = useTranslation();

  // This only happens when the only capture option is web, skip prompt page is
  // configured, and user hasn't enabled camera permissions yet
  if (props.skipPromptPage && enabledCaptureOptions.length === 0) {
    return <FullScreenLoadingSpinner />;
  }

  // Prefer the web camera recording over a stillfie
  if (enabledCaptureOptions.includes('web_camera')) {
    return <RecordView {...props} t={t} i18n={i18n} />;
  } else if (allowStillfieFallback || HolderClient.isDashboard) {
    return <StillfieView {...props} t={t} i18n={i18n} />;
  } else {
    return <Redirect to='/inquiry/support/camera' />;
  }
}

type SubViewProps = InquirySelfiePromptViewProps & Omit<WithTranslation, 'tReady'>;

function RecordView({
  assets,
  localizations,
  enabledCaptureOptions,
  handleDeviceHandoff,
  isProcessing,
  requireSelfieMatch,
  handleStartRecording,
  uiReady,
}: SubViewProps) {
  const { base } = useStepSelfieStyleContext();
  let prompt;
  let pictograph;

  switch (requireSelfieMatch) {
    case SelfieMatch.ProfileAndCenter:
    case SelfieMatch.ConfigurablePoses:
      prompt = localizations['selfie-prompt'];
      pictograph = (
        <CenteredPictograph height={120} width={120} aria-label='selfie pictograph'>
          <CustomAssetWithFallback
            customImage={assets['selfie-pictograph']}
            alt='selfie pictograph'
          >
            <CustomizablePictograph
              url='selfiePictographUrl'
              height='selfiePictographHeight'
              aria-label='selfie pictograph'
              themed={false}
            >
              <SelfiePictograph frameDuration={100}>
                <SelfiePictograph.Center frames={8} />
                <SelfiePictograph.Left1 />
                <SelfiePictograph.Left2 frames={8} />
                <SelfiePictograph.Left1 />
                <SelfiePictograph.Center />
                <SelfiePictograph.Right1 />
                <SelfiePictograph.Right2 frames={8} />
                <SelfiePictograph.Right1 />
              </SelfiePictograph>
            </CustomizablePictograph>
          </CustomAssetWithFallback>
        </CenteredPictograph>
      );
      break;

    case SelfieMatch.OnlyCenter:
      prompt = localizations['selfie-prompt-center'];
      pictograph = (
        <CenteredPictograph height={120} width={120} aria-label='selfie pictograph'>
          <CustomAssetWithFallback
            customImage={assets['selfie-center-pictograph']}
            alt='selfie center pictograph'
          >
            <CustomizablePictograph
              url='selfieCenterPictographUrl'
              height='selfieCenterPictographHeight'
              aria-label='selfie center pictograph'
              themed={false}
            >
              <SelfiePictograph>
                <SelfiePictograph.Center />
              </SelfiePictograph>
            </CustomizablePictograph>
          </CustomAssetWithFallback>
        </CenteredPictograph>
      );
      break;

    default:
      captureMessage('Invalid selfie match requirement');
      break;
  }

  return (
    <Container>
      <CustomAssetWithFallback customImage={assets['header-pictograph']} alt='header pictograph' />
      <Header>
        <ThemedTitle styles={base?.titleStyle}>
          <Markdown>{localizations['selfie-title']}</Markdown>
        </ThemedTitle>
        <ThemedParagraph styles={base?.textStyle}>
          {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
          <Markdown>{prompt}</Markdown>
        </ThemedParagraph>
      </Header>
      {pictograph}
      <PositionableButtonContainer>
        <CaptureOptionsContainer>
          {enabledCaptureOptions.map((option, index) => (
            <OptionBlock
              key={option}
              isPrimary={index === 0}
              option={option}
              handleDeviceHandoff={handleDeviceHandoff}
              handleStartRecording={handleStartRecording}
              isProcessing={isProcessing}
              loading={!uiReady}
              localizations={localizations}
            />
          ))}
        </CaptureOptionsContainer>
      </PositionableButtonContainer>
    </Container>
  );
}

interface OptionBlockProps
  extends Pick<
    InquirySelfiePromptViewProps,
    'handleDeviceHandoff' | 'handleStartRecording' | 'isProcessing' | 'localizations'
  > {
  isPrimary: boolean;
  loading: boolean;
  option: SelfieCaptureOptionDesktop | SelfieCaptureOptionMobile;
}

function OptionBlock({
  handleDeviceHandoff,
  handleStartRecording,
  isPrimary,
  isProcessing,
  localizations,
  option,
  loading,
}: OptionBlockProps) {
  const { buttons: buttonStyles } = useStepSelfieStyleContext();
  const buttonType = isPrimary ? ButtonType.Primary : ButtonType.Secondary;
  const buttonDisabled = isProcessing != null || loading;

  switch (option) {
    case 'web_camera':
      return (
        <OptionButton
          id='selfie-prompt__button--camera'
          data-test='selfie-prompt__button--camera'
          buttonType={buttonType}
          disabled={buttonDisabled}
          styles={buttonStyles?.[buttonType]}
          isPending={isProcessing === PromptOptions.SelectCamera}
          onClick={handleStartRecording}
        >
          {localizations['btn-submit']}
        </OptionButton>
      );

    case 'device_handoff':
      return (
        <OptionButton
          id='selfie-prompt__button--to-mobile'
          data-test='selfie-prompt__button--to-mobile'
          buttonType={buttonType}
          disabled={buttonDisabled}
          styles={buttonStyles?.[buttonType]}
          isPending={isProcessing === PromptOptions.DeviceHandoff}
          onClick={handleDeviceHandoff}
        >
          {localizations['btn-device-handoff']}
        </OptionButton>
      );

    default:
      assertNever(option);
  }
}

const StillfieHint = styled.div`
  display: flex;
  justify-content: center;
`;

const StillfieHintColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  width: 145px;

  font-size: ${({ theme }) => theme.fontSizeSmall};
  line-height: ${({ theme }) => theme.lineHeightSmall};
`;

const StillfieHintIconContainer = styled.div`
  position: absolute;
  top: 108px;
  right: -4px;
`;

const StillfieHintText = styled.p`
  width: 100px;
`;

function StillfieView({
  assets,
  localizations,
  enabledCaptureOptions,
  isProcessing,
  currentStep,
  handleDeviceHandoff,
  handleSelectImage,
  uiReady,
}: SubViewProps) {
  // We often get questions about whether someone saw the stillfie view or not so let's log it!
  useEffect(() => {
    EventsClient.logAmplitude('selfie.stillfie', { currentStep });
  }, [currentStep]);

  const { base, buttons: buttonStyles } = useStepSelfieStyleContext();

  let prompt;
  let pictograph;

  switch (currentStep) {
    case SelfieStep.Center:
      prompt = localizations['selfie-prompt-center'];
      pictograph = (
        <CenteredPictograph height={120} width={120} aria-label='stillfie center pictograph'>
          <CustomAssetWithFallback
            customImage={assets['selfie-center-pictograph']}
            alt='stillfie center pictograph'
          >
            <CustomizablePictograph
              url='selfieCenterPictographUrl'
              height='selfieCenterPictographHeight'
              aria-label='stillfie center pictograph'
              themed={false}
            >
              <StillfieHint>
                <StillfieHintColumn>
                  <SelfiePictograph>
                    <SelfiePictograph.Center />
                  </SelfiePictograph>
                </StillfieHintColumn>
              </StillfieHint>
            </CustomizablePictograph>
          </CustomAssetWithFallback>
        </CenteredPictograph>
      );
      break;

    case SelfieStep.Left:
      prompt = localizations['selfie-prompt-left'];
      pictograph = (
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        <CenteredPictograph height={null} width={null} aria-label='stillfie left pictograph'>
          <CustomAssetWithFallback
            customImage={assets['selfie-left-pictograph']}
            alt='stillfie left pictograph'
          >
            <CustomizablePictograph
              url='selfieLeftPictographUrl'
              height='selfieLeftPictographHeight'
              aria-label='stillfie left pictograph'
              themed={false}
            >
              <StillfieHint>
                <StillfieHintColumn>
                  <SelfiePictograph>
                    <SelfiePictograph.Left1 />
                  </SelfiePictograph>
                  <StillfieHintIconContainer>
                    <IconCircleFilledCheck />
                  </StillfieHintIconContainer>
                  <StillfieHintText>{localizations['stillfie-hint-left']}</StillfieHintText>
                </StillfieHintColumn>

                <Spacer width={3} />

                <StillfieHintColumn>
                  <SelfiePictograph>
                    <SelfiePictograph.Left3 />
                  </SelfiePictograph>
                  <StillfieHintIconContainer>
                    <IconCircleFilledX />
                  </StillfieHintIconContainer>
                  <StillfieHintText>{localizations['stillfie-hint-wrong']}</StillfieHintText>
                </StillfieHintColumn>
              </StillfieHint>
            </CustomizablePictograph>
          </CustomAssetWithFallback>
        </CenteredPictograph>
      );
      break;

    case SelfieStep.Right:
      prompt = localizations['selfie-prompt-right'];
      pictograph = (
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        <CenteredPictograph height={null} width={null} aria-label='stillfie right pictograph'>
          <CustomAssetWithFallback
            customImage={assets['selfie-right-pictograph']}
            alt='stillfie right pictograph'
          >
            <CustomizablePictograph
              url='selfieRightPictographUrl'
              height='selfieRightPictographHeight'
              aria-label='stillfie right pictograph'
              themed={false}
            >
              <StillfieHint>
                <StillfieHintColumn>
                  <SelfiePictograph>
                    <SelfiePictograph.Right1 />
                  </SelfiePictograph>
                  <StillfieHintIconContainer>
                    <IconCircleFilledCheck />
                  </StillfieHintIconContainer>
                  <StillfieHintText>{localizations['stillfie-hint-right']}</StillfieHintText>
                </StillfieHintColumn>

                <Spacer width={3} />

                <StillfieHintColumn>
                  <SelfiePictograph>
                    <SelfiePictograph.Right3 />
                  </SelfiePictograph>
                  <StillfieHintIconContainer>
                    <IconCircleFilledX />
                  </StillfieHintIconContainer>
                  <StillfieHintText>{localizations['stillfie-hint-wrong']}</StillfieHintText>
                </StillfieHintColumn>
              </StillfieHint>
            </CustomizablePictograph>
          </CustomAssetWithFallback>
        </CenteredPictograph>
      );
      break;
  }

  return (
    <>
      <CustomAssetWithFallback customImage={assets['header-pictograph']} alt='header pictograph' />
      <Header>
        <ThemedTitle styles={base?.titleStyle}>
          <Markdown>{localizations['stillfie-title']}</Markdown>
        </ThemedTitle>
        <ThemedParagraph styles={base?.textStyle}>
          <Markdown>{prompt}</Markdown>
        </ThemedParagraph>
      </Header>
      {pictograph}
      <Spacer height={3} />
      <PositionableButtonContainer>
        <CaptureOptionsContainer>
          <ButtonInputCapture
            id='selfie-prompt__button--stillfie'
            data-test='selfie-prompt__button--stillfie'
            buttonType={ButtonType.Primary}
            capture='user'
            disabled={isProcessing != null || !uiReady}
            isPending={isProcessing === PromptOptions.UploadImage}
            handleSelectImage={handleSelectImage}
            styles={buttonStyles?.[ButtonType.Primary]}
          >
            <IconCameraStyled />
          </ButtonInputCapture>
          {enabledCaptureOptions.includes('device_handoff') && (
            <OptionButton
              id='selfie-prompt__button--to-mobile'
              data-test='selfie-prompt__button--to-mobile'
              buttonType={ButtonType.Secondary}
              disabled={!!isProcessing || !uiReady}
              isPending={isProcessing === PromptOptions.DeviceHandoff}
              onClick={handleDeviceHandoff}
              styles={buttonStyles?.[ButtonType.Secondary]}
            >
              {localizations['btn-device-handoff']}
            </OptionButton>
          )}
        </CaptureOptionsContainer>
      </PositionableButtonContainer>
    </>
  );
}
